import { Component, EventEmitter, Input, Output } from "@angular/core";
import { OrgsService } from "@app2/account/orgs.service";
import { UserService } from "@app2/account/user.service";
import { hasAnyFraudSku } from "@app2/fraud/utils";
import { hasAnyRiskSku, hasRiskSysAppsSku } from "@app2/risk/utils";
import { ActiveNavSectionService } from "@app2/shared/services/active-nav-section.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { animate, animateChild, group, keyframes, query, state, style, transition, trigger } from "@angular/animations";
import { Observable } from "rxjs";
import { RiskModuleType } from "@app2/type-defs/risk/risk-types";
import { RiskService } from "@app2/risk/risk-shared/risk.service";
import { FeaturesService } from "@app2/shared/services/features.service";
import { Keys, StorageService } from "@app2/shared/services/storage.service";
import { RouterService } from "@app2/shared/services/router.service";

@UntilDestroy()
@Component({
    selector: "navigation-left-bar",
    template: `
        <nav class="left-bar">
            <div [@toggleMenu]="leftBarVisible" class="left-bar-container">
                <navbar-org-selector [@toggleContent]="leftBarVisible"
                                     [leftBarVisible]="leftBarVisible"></navbar-org-selector>
                <div class="left-bar-menu"
                     [@toggleContent]="leftBarVisible"
                     [ngClass]="leftBarVisible ? 'left-bar-expanded' : 'left-bar-collapsed'"
                     [ngSwitch]="showSubsection">
                    <div class="left-bar-menu-section" test-id="home-menu">
                        <span *ngIf="leftBarVisible" class="section-label">{{ 'HOME' | localize }}</span>
                        <ul class="left-bar-items">
                            <li [class.active]="navSectionService.isDashboardsActive$ | async"
                                *dsPermissionsRequired="'DASHBOARD_VIEW'">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(1)">
                                    <div>
                                        <i class="fal fa-chart-line"
                                           [matTooltip]="!leftBarVisible ? ('DASHBOARDS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'DASHBOARDS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 1 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 1" class="subsection-list">
                                    <li [class.active]="(navSectionService.isDefaultDashboardActive$ | async) || (navSectionService.isOldMainDashboardActive$ | async)"
                                        *dsPermissionsRequired="'DASHBOARD_VIEW'">
                                        <a dsRouterLink="/dashboards/default">{{ "ALL_DASHBOARDS" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isDashboardsListActive$ | async"
                                        *dsPermissionsRequired="'DASHBOARD_VIEW'">
                                        <a dsRouterLink="/dashboards">{{ 'REVEAL_DASHBOARDS' | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isLibraryDashboardsActive$ | async"
                                        *dsPermissionsRequired="'DASHBOARD_VIEW'">
                                        <a dsRouterLink="/dashboards/library">{{ 'LIBRARY' | localize }}</a>
                                    </li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isReportsActive$ | async"
                                *dsPermissionsRequired="'any: REPORT_VIEW, CHART_VIEW'"
                                test-id="main-reports-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(2)">
                                    <div>
                                        <i class="fal fa-chart-simple"
                                           [matTooltip]="!leftBarVisible ? ('REPORTS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'REPORTS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 2 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 2" class="subsection-list">
                                    <li [class.active]="navSectionService.isReportsListActive$ | async"
                                        *dsPermissionsRequired="'REPORT_VIEW'">
                                        <a dsRouterLink="/reports">{{ 'ALL_REPORTS' | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isReportTemplatesListActive$ | async"
                                        *dsPermissionsRequired="'REPORT_VIEW'">
                                        <a dsRouterLink="/reports/templates/list">{{ 'TEMPLATES' | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isChartsListActive$ | async"
                                        *dsPermissionsRequired="'CHART_VIEW'">
                                        <a dsRouterLink="/reports/charts/list">{{ 'CHARTS' | localize }}</a>
                                    </li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isTicketsActive$ | async"
                                *dsPermissionsRequired="'any: INCIDENT_VIEW, TASK_VIEW, TASK_SCHEDULE_VIEW'"
                                test-id="main-tickets-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(3)"
                                     test-id="module-tickets-description">
                                    <div>
                                        <i class="fal fa-ballot-check"
                                           [matTooltip]="!leftBarVisible ? ('TICKETS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'TICKETS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 3 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 3"
                                    class="subsection-list"
                                    test-id="tickets-subsection-list">
                                    <li [class.active]="navSectionService.isIncidentsActive$ | async"
                                        *dsPermissionsRequired="'INCIDENT_VIEW'">
                                        <a dsRouterLink="/tickets/incident">{{ 'INCIDENTS' | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isTasksActive$ | async"
                                        *dsPermissionsRequired="'TASK_VIEW'">
                                        <a dsRouterLink="/tickets/task">{{ 'TASKS' | localize }}</a>
                                    </li>
                                    <ng-container *featureGate="'universalTicketInbox'">
                                        <li [class.active]="navSectionService.isUniversalTicketInboxActive$ | async">
                                            <a dsRouterLink="/universal-ticket-inbox/incident">{{ "GLOBAL_TICKET_INBOX_TAB" | localize }}</a>
                                        </li>
                                    </ng-container>
                                    <ng-container *dsPermissionsRequired="'TRAC_USER'">
                                        <li [class.active]="navSectionService.isTicketTemplateActive$ | async">
                                            <a dsRouterLink="/ticket-templates">{{ "TICKET_TEMPLATES_TAB" | localize }}</a>
                                        </li>
                                    </ng-container>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isEventsActive$ | async"
                                *dsPermissionsRequired="'any: EVENT_VIEW, SAVED_SEARCH_VIEW, CLASSIFIER_VIEW'"
                                test-id="main-events-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(4)"
                                     test-id="module-events-description">
                                    <div>
                                        <i class="fal fa-laptop"
                                           [matTooltip]="!leftBarVisible ? ('EVENTS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'EVENTS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 4 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 4"
                                    class="subsection-list"
                                    test-id="events-subsection-list">
                                    <li [class.active]="navSectionService.isEventsListActive$ | async"
                                        *dsPermissionsRequired="'EVENT_VIEW'">
                                        <a dsRouterLink="/search">{{ 'ALL_EVENTS' | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isSavedQueriesActive$ | async"
                                        *dsPermissionsRequired="'SAVED_SEARCH_VIEW'">
                                        <a dsRouterLink="/search/saved">{{ "SAVED_QUERIES" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isClassifiersActive$ | async"
                                        *dsPermissionsRequired="'CLASSIFIER_VIEW'">
                                        <a dsRouterLink="/classifiers">{{ "BASE_CLASSIFIERS" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isCrossOrgSearchActive$ | async"
                                        *dsPermissionsRequired="'any: TRAC_ADMIN, TRAC_ENGINEER'">
                                        <a dsRouterLink="/search/cross-org-search">{{ "CROSS_ORG_SEARCH" | localize }}</a>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <div class="left-bar-menu-section" test-id="products-menu">
                        <span *ngIf="leftBarVisible" class="section-label">{{ 'PRODUCTS' | localize }}</span>
                        <ul class="left-bar-items">
                            <li [class.active]="(navSectionService.isAlertsActive$ | async)"
                                *dsPermissionsRequired="'any: ALERT_INBOX_VIEW, TRIGGER_VIEW, THREATMATCH_VIEW'"
                                test-id="main-alerts-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(5)">
                                    <div>
                                        <i class="fal fa-shield-halved"
                                           [matTooltip]="!leftBarVisible ? ('THREAT_SURVEILLANCE' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'THREAT_SURVEILLANCE' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 5 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 5" class="subsection-list">
                                    <li [class.active]="(navSectionService.isAlertInboxActive$ | async)"
                                        *dsPermissionsRequired="'ALERT_INBOX_VIEW'">
                                        <a dsRouterLink="/alerts/inbox">{{ "ALERT_INBOX" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isTriggersActive$ | async"
                                        *dsPermissionsRequired="'TRIGGER_VIEW'">
                                        <a dsRouterLink="/triggers">{{ "TRIGGERS" | localize}}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isTriggerSchedulesActive$ | async"
                                        *dsPermissionsRequired="'TRIGGER_VIEW'">
                                        <a dsRouterLink="/trigger-schedules">{{ "TRIGGER_SCHEDULES" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isLibraryTriggersActive$ | async"
                                        *dsPermissionsRequired="'TRIGGER_VIEW'">
                                        <a dsRouterLink="/triggers/library">{{ "DEFAULT_TRIGGERS" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isThreatMatchActive$ | async"
                                        *dsPermissionsRequired="'THREATMATCH_VIEW'">
                                        <a dsRouterLink="/threat">{{ "THREAT_MATCH" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isWatchlistsActive$ | async"
                                        *dsPermissionsRequired="'THREATMATCH_VIEW'">
                                        <a dsRouterLink="/watchlists">{{ "WATCHLISTS" | localize }}</a></li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isComplianceActive$ | async"
                                *dsPermissionsRequired="'any: CAT_POLICY_VIEW, CUSTOM_POLICY_VIEW'"
                                test-id="main-compliance-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(6)">
                                    <div>
                                        <i class="fal fa-scale-unbalanced"
                                           [matTooltip]="!leftBarVisible ? ('GOVERNANCE' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'GOVERNANCE' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 6 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 6" class="subsection-list">
                                    <li [class.active]="navSectionService.isTaskSchedulesActive$ | async"
                                        *dsPermissionsRequired="'TASK_SCHEDULE_VIEW'">
                                        <a dsRouterLink="/task-schedules">{{ "TASK_SCHEDULES" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isLibraryTaskSchedulesActive$ | async"
                                        *dsPermissionsRequired="'TASK_SCHEDULE_VIEW'">
                                        <a dsRouterLink="/library-task-schedules">{{ "LIBRARY_TASK_SCHEDULES_TAB" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isFrameworksActive$ | async"
                                        *dsPermissionsRequired="'POLICY_FRAMEWORK_VIEW'">
                                        <a dsRouterLink="/governance/frameworks"
                                           [queryParams]="{canRedirect: true}">{{"POLICY_FRAMEWORKS" | localize}}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isCustomPoliciesActive$ | async"
                                        *dsPermissionsRequired="'CUSTOM_POLICY_VIEW'">
                                        <a dsRouterLink="/governance/policies">{{"POLICY_POLICIES" | localize}}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isCATPoliciesActive$ | async"
                                        *dsPermissionsRequired="'CAT_POLICY_VIEW'">
                                        <a dsRouterLink="/governance/cat">{{"FFIEC_CAT" | localize}}</a>
                                    </li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isFraudActive$ | async"
                                *dsPermissionsRequired="'ALERT_INBOX_VIEW'"
                                test-id="main-fraud-container">
                                <div class="left-navbar-icon"
                                     (click)="hasFraudSku ? handleSubsection(7) : navigateToFraudPromoPage()">
                                    <div>
                                        <i class="fal fa-user-secret"
                                           [matTooltip]="!leftBarVisible ? ('FRAUD' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'FRAUD' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible && hasFraudSku"
                                       class="far"
                                       [ngClass]="showSubsection === 7 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 7" class="subsection-list">
                                    <li [class.active]="navSectionService.isFraudInboxActive$ | async"
                                        *dsPermissionsRequired="'ALERT_INBOX_VIEW'">
                                        <a dsRouterLink="/fraud/inbox">{{ "FRAUD_INBOX" | localize }}</a></li>
                                    <li [class.active]="navSectionService.isFraudTriggersActive$ | async"
                                        *dsPermissionsRequired="'TRIGGER_VIEW'">
                                        <a dsRouterLink="/fraud/triggers">{{ "TRIGGERS" | localize}}</a></li>
                                    <li [class.active]="navSectionService.isFraudLibraryTriggersActive$ | async"
                                        *dsPermissionsRequired="'TRIGGER_VIEW'">
                                        <a dsRouterLink="/fraud/library">{{ "DEFAULT_TRIGGERS" | localize }}</a></li>
                                    <li [class.active]="navSectionService.isFraudThreatmatchActive$ | async"
                                        *dsPermissionsRequired="'THREATMATCH_VIEW'">
                                        <a dsRouterLink="/fraud/threat">{{ "BASE_THREAT_MATCH" | localize }}</a></li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isRiskActive$ | async"
                                *dsPermissionsRequired="'any: RISK_VIEW'"
                                test-id="main-risk-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(8)">
                                    <div>
                                        <i class="fal fa-gauge-simple-low"
                                           [matTooltip]="!leftBarVisible ? ('RISK' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'RISK' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 8 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 8" class="subsection-list">
                                    <li *ngIf="hasRiskSku"
                                        [class.active]="navSectionService.isRiskRegisterActive$ | async">
                                        <a dsRouterLink="/risk/risks/register">{{ "RISK_REGISTER" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isRiskLibraryActive$ | async">
                                        <a dsRouterLink="/risk/risks/library">{{ "RISK_LIBRARY" | localize }}</a>
                                    </li>
                                    <li *ngIf="hasRiskSku"
                                        [class.active]="navSectionService.isControlsRegisterActive$ | async">
                                        <a dsRouterLink="/risk/controls/register">{{ "CONTROLS_REGISTER" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isControlsLibraryActive$ | async">
                                        <a dsRouterLink="/risk/controls/library">{{ "CONTROLS_LIBRARY" | localize }}</a>
                                    </li>
                                    <li [class.active]="navSectionService.isAssessmentsActive$ | async">
                                        <a dsRouterLink="/risk/assessments">{{ "RISK_ASSESSMENTS" | localize }}</a>
                                    </li>
                                    <li *ngIf="hasSystemsAppsSku && (isSystemsAppsSelected$ | async)"
                                        [class.active]="navSectionService.isInventoryRegisterActive$ | async">
                                        <a dsRouterLink="/risk/inventory/register">{{ "INVENTORY" | localize }}</a>
                                    </li>
                                    <li *ngIf="hasSystemsAppsSku && (isSystemsAppsSelected$ | async)"
                                        [class.active]="navSectionService.isInventoryLibraryActive$ | async">
                                        <a dsRouterLink="/risk/inventory/library">{{ "INVENTORY_LIBRARY" | localize }}</a>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <div class="left-bar-menu-section" test-id="more-menu">
                        <span *ngIf="leftBarVisible" class="section-label">{{ 'SHOW_MORE' | localize }}</span>
                        <ul class="more-menu left-bar-items">
                            <li [class.active]="navSectionService.isAssetsActive$ | async"
                                *dsPermissionsRequired="'any: ASSET_VIEW'"
                                test-id="main-assets-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(9)">
                                    <div>
                                        <i class="fal fa-cubes"
                                           [matTooltip]="!leftBarVisible ? ('ASSETS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'ASSETS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 9 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 9" class="subsection-list">
                                    <li [class.active]="navSectionService.isAssetsListActive$ | async"
                                        *dsPermissionsRequired="'ASSET_VIEW'">
                                        <a dsRouterLink="/assets">{{ "ALL_ASSETS" | localize }}</a></li>
                                </ul>
                            </li>
                            <li [class.active]="navSectionService.isSettingsActive$ | async"
                                test-id="main-settings-container">
                                <div class="left-navbar-icon"
                                     (click)="handleSubsection(10)">
                                    <div>
                                        <i class="fal fa-gear"
                                           [matTooltip]="!leftBarVisible ? ('SETTINGS' | localize) : ''"></i>
                                        <span *ngIf="leftBarVisible">{{ 'SETTINGS' | localize }}</span>
                                    </div>
                                    <i *ngIf="leftBarVisible"
                                       class="far"
                                       [ngClass]="showSubsection === 10 ? 'fa-chevron-up' : 'fa-chevron-right'"></i>
                                </div>
                                <ul *ngSwitchCase="leftBarVisible && 10" class="subsection-list">
                                    <li [class.active]="navSectionService.isUsersListActive$ | async"
                                        *dsPermissionsRequired="'USER_VIEW'">
                                        <a dsRouterLink="/settings/users">{{"SETTINGS_USERS" | localize}}</a></li>
                                    <li [class.active]="navSectionService.isInputTokensListActive$ | async"
                                        *dsPermissionsRequired="'AUTH_TOKEN_VIEW'">
                                        <a dsRouterLink="/settings/input-tokens">{{"SETTINGS_SOURCES" | localize}}</a></li>
                                    <li [class.active]="navSectionService.isIntegrationsActive$ | async"
                                        *dsPermissionsRequired="'INTEGRATION_VIEW'">
                                        <a dsRouterLink="/settings/integrations">{{'SETTINGS_INTEGRATIONS' | localize}}</a></li>
                                    <li [class.active]="navSectionService.isDataRestrictionsActive$ | async"
                                        *dsPermissionsRequired="'DATA_RESTRICTION_VIEW'">
                                        <a dsRouterLink="/settings/data-restrictions">{{'DATA_RESTRICTIONS' | localize}}</a></li>
                                    <li [class.active]="navSectionService.isUserProfileActive$ | async">
                                        <a dsRouterLink="/settings/profile">{{"SETTINGS_PROFILE" | localize}}</a></li>
                                    <li [class.active]="navSectionService.isOrgSettingsActive$ | async"
                                        *dsPermissionsRequired="'GLOBAL_SETTINGS_VIEW'">
                                        <a dsRouterLink="/settings/org-settings">{{"ORG_SETTINGS" | localize}}</a></li>
                                    <li [class.active]="navSectionService.isRolesListActive$ | async"
                                        *dsPermissionsRequired="'USER_VIEW'">
                                        <a dsRouterLink="/settings/roles">{{"ROLES" | localize}}</a></li>
                                    <ng-container *featureGate="'escalationPage'">
                                        <li [class.active]="navSectionService.isEscalationPageActive$ | async"
                                            *dsPermissionsRequired="'ESCALATION_VIEW'">
                                            <a dsRouterLink="/settings/escalation">{{"ESCALATION" | localize}}</a></li>
                                    </ng-container>
                                    <li [class.active]="navSectionService.isGlobalTagSearchActive$ | async"
                                        *dsPermissionsRequired="'TAG_VIEW'">
                                        <a dsRouterLink="/settings/tags">{{"SETTINGS_TAGS" | localize}}</a></li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </nav>
    `,
    styles: [
        `
            @import "/src/styles/colors";
            @import "/src/styles/settings";

            .left-bar-container {
                display: flex;
                flex-direction: column;
                height: 100%;
                padding: 24px;

                ul {
                    margin: 12px 0 0 0;
                    list-style-type: none;
                }
            }

            .left-bar-menu {
                margin-top: 24px;
                overflow-y: auto;
                max-height: 832px;

                &.left-bar-expanded {
                    .left-bar-menu-section {
                        margin-top: 8px;
                    }

                    .section-label {
                        margin-left: 16px;
                    }

                    .left-navbar-icon {
                        display: flex;
                        justify-content: space-between;
                        padding: 16px;

                        &:hover {
                            border-radius: 4px;
                            background-color: $ds-blue-1;
                        }

                        span {
                            white-space: nowrap;
                            margin-left: 8px;
                        }
                    }

                    li {
                        &.active div.left-navbar-icon {
                            color: $ds-blue-6;
                        }
                    }
                }

                &.left-bar-collapsed {
                    i {
                        margin-bottom: 40px;
                        font-size: 1.25rem;
                    }

                    li.active {
                        color: $ds-blue-6;
                    }

                    li:hover {
                        color: $ds-blue-7;
                    }
                }
            }

            .left-bar-items {
                ul {
                    margin: 8px 0;
                }

                .subsection-list {
                    margin: 12px;

                    li {
                        margin-bottom: 8px;

                        span {
                            font-size: 0.875rem;
                        }

                        a {
                            display: block;
                            padding: 12px 16px;
                            color: $gray-10;
                        }

                        &:hover {
                            background: $ds-blue-1;
                            border-radius: 4px;
                        }

                        &.active {
                            background: $gray-3;
                            border-radius: 4px;
                        }
                    }
                }
            }

            .left-bar-menu:not(.left-bar-expanded) {
                .left-bar-items .left-navbar-icon {
                    display: flex;
                    justify-content: center;
                }
            }

            navbar-org-selector {
                display: flex;
                align-items: center;
            }

            .section-label {
                text-transform: uppercase;
                font-size: 0.75rem;
                font-weight: normal;
                color: $gray-6;
            }

            .more-menu li {
                a {
                    color: $gray-6;

                    &:hover {
                        color: $ds-blue-6;
                    }
                }

                &.active a {
                    color: $ds-blue-6;
                }
            }
        `],
    animations: [
        trigger("toggleMenu", [
            state("true", style({ width: "19.25rem" })),
            state("false", style({ width: "5.5rem" })),
            transition("* <=> *", [
                group([
                    animate("150ms ease-in"),
                    query("@toggleContent", animateChild()),
                ]),
            ]),
        ]),
        trigger("toggleContent", [
            transition("false => true", animate("150ms", keyframes([
                style({ display: "none", offset: 0 }),
            ]))),
        ]),
    ],
})
export class NavigationLeftBarComponent {
    @Input() leftBarVisible: boolean;
    @Output() showLeftBar = new EventEmitter();

    showSubsection: number;
    hasRiskSku: boolean;
    hasSystemsAppsSku: boolean;
    hasFraudSku: boolean;

    constructor(public navSectionService: ActiveNavSectionService,
                public riskService: RiskService,
                private userService: UserService,
                private orgsService: OrgsService,
                private featuresService: FeaturesService,
                private routerService: RouterService,
                private readonly storageService: StorageService) {
        const orgSkus = orgsService.getCurrentOrg().productSkus;
        this.hasRiskSku = hasAnyRiskSku(orgSkus);
        this.hasSystemsAppsSku = hasRiskSysAppsSku(orgSkus);
        this.hasFraudSku = hasAnyFraudSku(orgsService.getCurrentOrg().productSkus);

        // Refreshing the tab will still keep track of which subsection is opened
        const tempSubsectionNum = this.storageService.getShared(Keys.showSubsection);
        if (tempSubsectionNum) {
            this.showSubsection = tempSubsectionNum;
        }
    }

    handleSubsection(subsectionNum: number) {
        // If left bar is collapsed and the user clicks the icon, then open the left bar
        if (!this.leftBarVisible) {
            this.leftBarVisible = true;
            this.showLeftBar.emit();
        }

        // If a different subsection is clicked, switch subsections
        // If the same subsection is clicked, the current subsection collpases
        this.showSubsection = this.showSubsection !== subsectionNum ? subsectionNum : 0;
        this.storageService.setShared(Keys.showSubsection, this.showSubsection);
    }

    navigateToFraudPromoPage() {
        this.routerService.navigate(["/fraud/offering"]);
    }

    get isSystemsAppsSelected$(): Observable<boolean> {
        return this.riskService.isModuleSelected$(RiskModuleType.SYSTEMS_AND_APPLICATIONS);
    }
}
